/* eslint-disable react/no-array-index-key -- Native Puck array slots are identified by their positional field paths. */

import React, { useRef, useState } from 'react';
import type { ComponentConfig } from '@puckeditor/core';
import styled from 'styled-components';

import { INHERIT_COLOR, THEME_SURFACE_TEXT_COLOR } from '../fields/container-colors';
import { isHexColor } from '../fields/hex-color';
import { renderFieldGroups } from '../fields/field-groups';
import type { FieldGroupDefinition } from '../fields/field-groups';
import { createCustomColorField } from '../fields/widgets/custom-color-field';
import { interpolateFromPuck } from '../interpolation';
import { lookup, TEXT_COLOR_ROLES } from '../fields/tokens';

import { starterChild } from './slot';
import type { SlotProps } from './slot';

export type TabContent = { label: string; content?: React.FC<SlotProps> };
export type TabsProps = {
  tabs: TabContent[];
  labelColor?: string;
  activeColor?: string;
};

const DEFAULT_TABS: TabContent[] = [1, 2].map((number) => ({
  label: `Tab ${number}`,
  content: ([starterChild('Paragraph', { text: '<p>Add your content here.</p>' })] as unknown) as React.FC<SlotProps>,
}));

const TAB_TEXT_COLORS = { ...TEXT_COLOR_ROLES, text: THEME_SURFACE_TEXT_COLOR };
const TAB_TEXT_COLOR_OPTIONS = [
  { label: 'Text', value: 'text' },
  { label: 'Secondary', value: 'secondary' },
  { label: 'Muted', value: 'muted' },
  { label: 'Inverted', value: 'inverted' },
  { label: 'Link', value: 'link' },
];

function tabsColorField(
  options: Array<{ label: string; value: string }>,
  customValue: string
): ReturnType<typeof createCustomColorField> {
  return createCustomColorField({
    options,
    testIdPrefix: 'tabs-color',
    cssPrefix: '--dcm-cms-tabs-color-field',
    customDefault: '#ffffff',
    isNamedOption: (value) => value === INHERIT_COLOR || options.some((option) => option.value === value),
    defaultValue: INHERIT_COLOR,
    mode: { emptyValue: INHERIT_COLOR, emptyLabel: 'Inherit', customValue },
  });
}

const TabTextColorField = tabsColorField(TAB_TEXT_COLOR_OPTIONS, 'text');
const TABS_COLOR_AI_SCHEMA = {
  type: 'string',
  description: 'Use inherit for the default theme color, a named theme color role, or a complete hex color.',
} as const;

function resolveTabsColor(value: unknown, roles: Readonly<Record<string, string>>): string | undefined {
  if (typeof value !== 'string' || value === INHERIT_COLOR) return undefined;
  if (isHexColor(value)) return value;
  return lookup<string | undefined>(roles, value, undefined);
}

const Wrapper = styled.div`
  min-width: 0;
  width: 100%;
`;

const TabList = styled.div`
  display: flex;
  overflow-x: auto;
  gap: var(--dcm-cms-tabs-gap, 8px);
`;

const TabButton = styled.button`
  flex: 0 0 auto;
  max-width: 100%;
  overflow-wrap: anywhere;
  padding: var(--dcm-cms-tabs-tab-padding, 12px 16px);
  background: var(--dcm-cms-tabs-background, transparent);
  color: var(--cms-tabs-color, var(--dcm-cms-tabs-color, inherit));
  font-family: var(--dcm-cms-tabs-font-family, inherit);
  font-size: var(--dcm-cms-tabs-font-size, 16px);
  border: 0;
  border-bottom: 2px solid var(--dcm-cms-tabs-inactive-border-color, transparent);
  cursor: pointer;

  &[aria-selected='true'] {
    border-bottom-color: var(
      --cms-tabs-active-color,
      var(--dcm-cms-tabs-active-color, var(--dcm-color-primary, #0b99e6))
    );
  }

  &:focus-visible {
    outline: var(--dcm-cms-tabs-focus-outline, 3px solid var(--dcm-color-primary, #0b99e6));
    outline-offset: -3px;
  }
`;

const Panel = styled.div`
  min-width: 0;
  padding: var(--dcm-cms-tabs-panel-padding, 16px 0);

  &:focus-visible {
    outline: var(--dcm-cms-tabs-focus-outline, 3px solid var(--dcm-color-primary, #0b99e6));
    outline-offset: -3px;
  }
`;

const EditorHeading = styled.h3`
  color: var(--dcm-cms-tabs-editor-heading-color, inherit);
  font-family: var(--dcm-cms-tabs-font-family, inherit);
  font-size: var(--dcm-cms-tabs-font-size, 16px);
  margin: 0 0 12px;
`;

function Tabs({
  id,
  tabs,
  labelColor,
  activeColor,
  isEditing,
}: TabsProps & { id: string; isEditing: boolean }): JSX.Element {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);
  const activeIndex = Math.min(selectedIndex, tabs.length - 1);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number): void => {
    const targets: Record<string, number> = {
      ArrowRight: (index + 1) % tabs.length,
      ArrowLeft: (index - 1 + tabs.length) % tabs.length,
      Home: 0,
      End: tabs.length - 1,
    };
    const next = lookup<number>(targets, event.key, -1);
    if (next === -1) return;
    event.preventDefault();
    setSelectedIndex(next);
    buttons.current[next]?.focus();
  };

  const resolvedLabelColor = resolveTabsColor(labelColor, TAB_TEXT_COLORS);
  const resolvedActiveColor = resolveTabsColor(activeColor, TAB_TEXT_COLORS);
  const style = {
    ...(resolvedLabelColor !== undefined ? { '--cms-tabs-color': resolvedLabelColor } : {}),
    ...(resolvedActiveColor !== undefined ? { '--cms-tabs-active-color': resolvedActiveColor } : {}),
  } as React.CSSProperties;

  return (
    <Wrapper style={Object.keys(style).length > 0 ? style : undefined}>
      <TabList role='tablist' aria-label='Tabs'>
        {tabs.map((tab, index) => (
          <TabButton
            key={`tab-${index}`}
            ref={(button) => {
              buttons.current[index] = button;
            }}
            type='button'
            role='tab'
            id={`cms-tabs-${id}-tab-${index + 1}`}
            aria-controls={`cms-tabs-${id}-panel-${index + 1}`}
            aria-selected={index === activeIndex}
            tabIndex={index === activeIndex ? 0 : -1}
            disabled={isEditing}
            onClick={() => setSelectedIndex(index)}
            onKeyDown={(event) => handleKeyDown(event, index)}
          >
            {tab.label}
          </TabButton>
        ))}
      </TabList>
      {tabs.map((tab, index) => {
        const Slot = typeof tab.content === 'function' ? tab.content : undefined;
        return (
          <Panel
            key={`panel-${index}`}
            role={isEditing ? 'region' : 'tabpanel'}
            id={`cms-tabs-${id}-panel-${index + 1}`}
            aria-labelledby={isEditing ? `cms-tabs-${id}-editor-${index + 1}` : `cms-tabs-${id}-tab-${index + 1}`}
            hidden={!isEditing && index !== activeIndex}
            tabIndex={isEditing ? undefined : 0}
          >
            {isEditing && <EditorHeading id={`cms-tabs-${id}-editor-${index + 1}`}>{tab.label}</EditorHeading>}
            {Slot && <Slot minEmptyHeight={80} />}
          </Panel>
        );
      })}
    </Wrapper>
  );
}

const TABS_FIELD_GROUPS: readonly FieldGroupDefinition<keyof TabsProps>[] = [
  { label: 'Appearance', fields: ['labelColor', 'activeColor'] },
];

export const tabsConfig: ComponentConfig<TabsProps> = {
  label: 'Tabs',
  ai: {
    instructions:
      'A tabbed content block for locations or related information. Author two to eight tabs, each with a short label and its own content slot. Use Section for background and spacing, and Grid Layout or Split Banner inside a tab for location cards. Color follows the section theme by default. Use labelColor (Tab label color) and activeColor (Active-tab color) overrides when tabs need different contrast on their surrounding surface. The first tab opens on the published page. All panels stay visible while editing so every slot can be edited. Do not hide essential shopping information behind tabs. No URL hash navigation.',
  },
  fields: {
    tabs: {
      type: 'array',
      label: 'Tabs',
      min: 2,
      max: 8,
      arrayFields: { label: { type: 'text', label: 'Label' }, content: { type: 'slot' } },
      defaultItemProps: { label: 'New tab', content: ([] as unknown) as React.FC<SlotProps> },
      getItemSummary: (tab) => (typeof tab.label === 'string' ? tab.label.trim() : '') || 'Tab',
    },
    labelColor: {
      type: 'custom',
      label: 'Tab label color',
      render: TabTextColorField,
      ai: { schema: TABS_COLOR_AI_SCHEMA },
    },
    activeColor: {
      type: 'custom',
      label: 'Active-tab color',
      render: TabTextColorField,
      ai: { schema: TABS_COLOR_AI_SCHEMA },
    },
  },
  defaultProps: {
    tabs: DEFAULT_TABS,
    labelColor: INHERIT_COLOR,
    activeColor: INHERIT_COLOR,
  },
  renderFields: ({ fields }) => (
    <>
      {fields.tabs}
      {renderFieldGroups(fields, TABS_FIELD_GROUPS)}
    </>
  ),
  render: ({ id, tabs = DEFAULT_TABS, labelColor = INHERIT_COLOR, activeColor = INHERIT_COLOR, puck }) => {
    const values: unknown = tabs;
    const safeTabs = (Array.isArray(values) ? values : [])
      .slice(0, 8)
      .filter((tab: unknown): tab is TabContent => tab !== null && typeof tab === 'object');
    return (
      <Tabs
        id={id}
        tabs={safeTabs.map((tab, index) => ({
          ...tab,
          label: interpolateFromPuck(typeof tab.label === 'string' ? tab.label : '', puck).trim() || `Tab ${index + 1}`,
        }))}
        labelColor={labelColor}
        activeColor={activeColor}
        isEditing={Boolean(puck.isEditing)}
      />
    );
  },
};
